/**
 * Pure eligibility predicates for the modals owned by `global-modals-cloud-only.tsx`.
 *
 * They live here rather than inline in the component so they can be exercised
 * directly: as inline expressions inside a ~900-line component that mounts the
 * whole billing/entitlement stack, the only way to test them was to restate them
 * in the test file, which is not a test of anything. Keep new conditions in this
 * module and have the component read them.
 *
 * Eligibility for the *queued* modals (network/firewall, team-member specialty,
 * the queued self-onboarding flow) is not here — it belongs to
 * `launchModalsConfigAtom`, which owns the queue and is tested against directly.
 */
import {
  computeNeedsSelfOnboardingDetails,
  type SelfOnboardingDetailsInput,
} from '@heidi/growth/data';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { isEmpty } from 'lodash-es';

/**
 * An integration user on a tier below Pro+ has to upgrade before any onboarding
 * or details modal is worth showing. Both Pro+ tiers clear the bar.
 */
export const computeIntegrationAndNeedsUpgrade = ({
  hasIntegration,
  userTier,
}: {
  hasIntegration: boolean;
  userTier: SubscriptionTier | null | undefined;
}): boolean =>
  hasIntegration &&
  !!userTier &&
  userTier !== SubscriptionTier.PRO_PLUS &&
  userTier !== SubscriptionTier.CLINICIAN_PRO_PLUS;

/** Routes that run their own onboarding UI, so a modal on top would collide. */
const ONBOARDING_OWNED_PATHS = [
  '/scribe/new-user',
  '/new-team-user',
  '/organization-access-required',
  '/account-suspended',
] as const;

export const isOnOnboardingOwnedPath = (pathname: string): boolean =>
  (ONBOARDING_OWNED_PATHS as readonly string[]).includes(pathname);

export interface LegacyOnboardingDetailsModalInput {
  isUserSuccess: boolean;
  hasRegion: boolean;
  pathname: string;
  integrationAndNeedsUpgrade: boolean;
  isDesktopConnectivityOnline: boolean;
  hasToken: boolean;
  isKindePropertiesPending: boolean;
  isKindePropertiesError: boolean;
}

/**
 * Non-dismissible "enter your details" modal for a loaded user with no region.
 */
export const computeShouldShowLegacyOnboardingDetailsModal = ({
  isUserSuccess,
  hasRegion,
  pathname,
  integrationAndNeedsUpgrade,
  isDesktopConnectivityOnline,
  hasToken,
  isKindePropertiesPending,
  isKindePropertiesError,
}: LegacyOnboardingDetailsModalInput): boolean =>
  isUserSuccess &&
  !hasRegion &&
  !isOnOnboardingOwnedPath(pathname) &&
  pathname !== '/dictate-onboarding' &&
  !integrationAndNeedsUpgrade &&
  isDesktopConnectivityOnline &&
  hasToken &&
  !isKindePropertiesPending &&
  !isKindePropertiesError;

export interface UserSelfOnboardingModalInput extends SelfOnboardingDetailsInput {
  pathname: string;
  isWidgetAuthFlow: boolean;
  isDesktopConnectivityOnline: boolean;
  hasToken: boolean;
  isKindePropertiesPending: boolean;
  isKindePropertiesError: boolean;
  isJoinedTeamsPending: boolean;
}

/**
 * Old (non-queued) self-onboarding flow. The queued flow is decided in
 * `launchModalsConfigAtom`; this one renders directly from the component.
 */
export const computeShouldShowUserSelfOnboardingModal = ({
  canSelfOnboardTeam,
  hasTeam,
  hasJoinableTeams,
  isUserSpecialtyEmpty,
  isUserFirstNameEmpty,
  isUserLastNameEmpty,
  pathname,
  isWidgetAuthFlow,
  isDesktopConnectivityOnline,
  hasToken,
  isKindePropertiesPending,
  isKindePropertiesError,
  isJoinedTeamsPending,
}: UserSelfOnboardingModalInput): boolean =>
  computeNeedsSelfOnboardingDetails({
    canSelfOnboardTeam,
    hasTeam,
    hasJoinableTeams,
    isUserSpecialtyEmpty,
    isUserFirstNameEmpty,
    isUserLastNameEmpty,
  }) &&
  !isOnOnboardingOwnedPath(pathname) &&
  pathname !== '/join-team' &&
  !isWidgetAuthFlow &&
  isDesktopConnectivityOnline &&
  hasToken &&
  !isKindePropertiesPending &&
  !isKindePropertiesError &&
  !isJoinedTeamsPending;

export interface OnboardingModalInput {
  isJoinedTeamsPending: boolean;
  hasTeam: boolean;
  canSelfOnboardTeam: boolean;
  hasRegion: boolean;
  hasToken: boolean;
  isUserLoaded: boolean;
  userOrganisationInfo: unknown;
  integrationAndNeedsUpgrade: boolean;
  hasVisitedNewUserRoute: boolean;
  hasCompletedOnboarding: boolean;
  /** The in-app onboarding still has to run; it owns the organisation step. */
  isOnboardingPending: boolean;
  isDesktopConnectivityOnline: boolean;
  pathname: string;
}

/**
 * Team-creation onboarding for a user who cannot self-onboard.
 *
 * `userOrganisationInfo` is gated with `isEmpty`, not a null check, and the
 * difference is load-bearing: an organisation response that comes back as `{}`
 * still counts as "no organisation" and the modal shows. Simplifying this to
 * `== null` changes behaviour for exactly that case.
 */
export const computeShouldShowOnboardingModal = ({
  isJoinedTeamsPending,
  hasTeam,
  canSelfOnboardTeam,
  hasRegion,
  hasToken,
  isUserLoaded,
  userOrganisationInfo,
  integrationAndNeedsUpgrade,
  hasVisitedNewUserRoute,
  hasCompletedOnboarding,
  isOnboardingPending,
  isDesktopConnectivityOnline,
  pathname,
}: OnboardingModalInput): boolean =>
  !isJoinedTeamsPending &&
  !hasTeam &&
  !canSelfOnboardTeam &&
  hasRegion &&
  hasToken &&
  isUserLoaded &&
  isEmpty(userOrganisationInfo) &&
  !integrationAndNeedsUpgrade &&
  !hasVisitedNewUserRoute &&
  !hasCompletedOnboarding &&
  !isOnboardingPending &&
  isDesktopConnectivityOnline &&
  !isOnOnboardingOwnedPath(pathname) &&
  pathname !== '/dictate-onboarding';

export interface ReverseTrialModalsInput {
  isInReverseTrial: boolean;
  isWidgetAuthFlow: boolean;
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: boolean;
  isScribePlusUser: boolean;
}

/**
 * Reverse-trial surfaces. Once the Evidence launch trial/upgrade flows are on,
 * only Scribe+ signups keep the old reverse-trial modals.
 */
export const computeShouldShowReverseTrialModals = ({
  isInReverseTrial,
  isWidgetAuthFlow,
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled,
  isScribePlusUser,
}: ReverseTrialModalsInput): boolean =>
  isInReverseTrial &&
  !isWidgetAuthFlow &&
  (!isEvidenceLaunchTrialAndUpgradeFlowsEnabled || isScribePlusUser);

export interface LegacyTrialEndModalInput {
  isBillingInfoLoaded: boolean;
  hasEvidenceTrialEnded: boolean;
  isTrialCancelledByUser: boolean;
  clinicianTrialEnded: boolean;
  userTier: SubscriptionTier | null | undefined;
  /** The Heidi II Clinician trial end flow owns this member's trial end, so it shows its own modal. */
  isClinicianTrialEndedByHeidi2Flow: boolean;
}

/**
 * Drives `appStateForModals.showEndTrialModal`.
 *
 * FREE-only keeps upgraded Scribe+/PRO users out. Do NOT also require a root
 * `trial_end_date`: after a Stripe cancel/expiry the backend projects
 * `trial_end_date: null` whenever `is_in_free_trial` is false, so that guard
 * blocked legitimate FREE expired reverse-trial users and broke the trial-end
 * plan-selection modal.
 */
export const computeShowLegacyTrialEndModal = ({
  isBillingInfoLoaded,
  hasEvidenceTrialEnded,
  isTrialCancelledByUser,
  clinicianTrialEnded,
  userTier,
  isClinicianTrialEndedByHeidi2Flow,
}: LegacyTrialEndModalInput): boolean =>
  isBillingInfoLoaded &&
  ((hasEvidenceTrialEnded && !isTrialCancelledByUser) ||
    (clinicianTrialEnded &&
      userTier === SubscriptionTier.FREE &&
      !isClinicianTrialEndedByHeidi2Flow));
